import React, { useState } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

interface DebateTurn {
  id: string;
  agent_side: 'pro' | 'con';
  phase: 'research' | 'opening' | 'rebuttal' | 'closing';
  content: string;
  isStreaming?: boolean;
}

interface LiveDebateProps {
  topic: string;
  turns: DebateTurn[];
  currentPhase: string;
  streamingContent: { pro: string; con: string };
  isGenerating: boolean;
  isPaused?: boolean;
  onPause?: () => void;
  onResume?: () => void;
  onAbort?: () => void;
  onRegenerate?: () => void;
  showControls?: boolean;
  hasSummary?: boolean;
  summaryText?: string;
  proLabel?: string;
  conLabel?: string;
}

const PHASES = [
  { key: 'research', n: '01', label: 'Research & Evidence' },
  { key: 'opening', n: '02', label: 'Opening Statements' },
  { key: 'rebuttal', n: '03', label: 'Rebuttals' },
  { key: 'closing', n: '04', label: 'Closing Arguments' },
] as const;

type PhaseKey = (typeof PHASES)[number]['key'];
type PhaseStatus = 'pending' | 'active' | 'complete';

const SIDE_STYLES = {
  pro: {
    rule: 'bg-steel-blue',
    text: 'text-steel-blue',
    caret: 'bg-steel-blue',
    prose:
      'prose prose-sm max-w-none break-words prose-p:text-charcoal/80 prose-p:leading-relaxed prose-p:my-3 prose-strong:text-charcoal prose-strong:font-semibold prose-ul:my-3 prose-li:text-charcoal/80 prose-li:my-1 prose-blockquote:border-steel-blue prose-blockquote:text-charcoal/70 prose-headings:text-charcoal',
  },
  con: {
    rule: 'bg-vermilion',
    text: 'text-vermilion',
    caret: 'bg-vermilion',
    prose:
      'prose prose-sm max-w-none break-words prose-p:text-charcoal/80 prose-p:leading-relaxed prose-p:my-3 prose-strong:text-charcoal prose-strong:font-semibold prose-ul:my-3 prose-li:text-charcoal/80 prose-li:my-1 prose-blockquote:border-vermilion prose-blockquote:text-charcoal/70 prose-headings:text-charcoal',
  },
} as const;

/** One side's column inside a phase row. Always mounted while the phase shows. */
const SideColumn: React.FC<{
  side: 'pro' | 'con';
  label: string;
  turn: DebateTurn | null;
  streamingText: string;
  showThinking: boolean;
}> = ({ side, label, turn, streamingText, showThinking }) => {
  const s = SIDE_STYLES[side];

  return (
    <div className={side === 'pro' ? 'border-b border-hairline p-6 md:border-b-0 md:border-r' : 'p-6'}>
      <div className="mb-4 flex items-center gap-2">
        <span className={`h-3 w-1 flex-shrink-0 rounded-full ${s.rule}`} />
        <span className={`font-mono text-xs font-medium uppercase tracking-wider ${s.text}`}>
          {label}
        </span>
      </div>

      <div className={s.prose} style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
        {turn ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.content}</ReactMarkdown>
        ) : streamingText ? (
          <>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
            <span className={`streaming-caret ${s.caret}`} aria-hidden />
          </>
        ) : showThinking ? (
          <div className="flex items-center gap-2.5 py-1 text-ink-muted">
            <span className="flex gap-1">
              {[0, 1, 2].map(i => (
                <span
                  key={i}
                  className={`h-1.5 w-1.5 animate-pulse-dot rounded-full ${s.rule}`}
                  style={{ animationDelay: `${i * 180}ms` }}
                />
              ))}
            </span>
            <span className="text-sm">Preparing argument…</span>
          </div>
        ) : (
          <p className="text-sm italic text-charcoal/30">Waiting…</p>
        )}
      </div>
    </div>
  );
};

const LiveDebate: React.FC<LiveDebateProps> = ({
  topic,
  turns,
  currentPhase,
  streamingContent,
  isGenerating,
  isPaused = false,
  onPause,
  onResume,
  onAbort,
  onRegenerate,
  showControls = false,
  hasSummary = false,
  summaryText = '',
  proLabel = 'Supporting',
  conLabel = 'Opposing',
}) => {
  // Manually collapsed completed phases (keyed by phase, plus 'summary')
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const turnsByPhase = turns.reduce((acc, turn) => {
    if (!acc[turn.phase]) acc[turn.phase] = { pro: null, con: null };
    acc[turn.phase][turn.agent_side] = turn;
    return acc;
  }, {} as Record<string, { pro: DebateTurn | null; con: DebateTurn | null }>);

  const phaseMatchesCurrent = (phase: PhaseKey) =>
    currentPhase.toLowerCase().includes(phase);

  const getPhaseStatus = (phase: PhaseKey): PhaseStatus => {
    const phaseTurns = turnsByPhase[phase];
    const bothDone = Boolean(phaseTurns?.pro && phaseTurns?.con);
    if (bothDone && !(isGenerating && phaseMatchesCurrent(phase))) return 'complete';
    if (
      (isGenerating && phaseMatchesCurrent(phase)) ||
      (phaseTurns && !bothDone)
    ) {
      return 'active';
    }
    return 'pending';
  };

  const completedPhaseCount = PHASES.filter(
    p => turnsByPhase[p.key]?.pro && turnsByPhase[p.key]?.con,
  ).length;
  const stagesDone = completedPhaseCount + (hasSummary ? 1 : 0);

  const summaryStatus: PhaseStatus = hasSummary
    ? 'complete'
    : isGenerating && currentPhase.toLowerCase().includes('summary')
    ? 'active'
    : 'pending';

  const toggleCollapsed = (phase: string) =>
    setCollapsed(prev => ({ ...prev, [phase]: !prev[phase] }));

  return (
    <MotionConfig reducedMotion="user">
      <div className="mx-auto w-full max-w-7xl">
        {/* ── Topic header ── */}
        <div className="card-frame mb-6 overflow-hidden">
          <div className="flex items-start justify-between gap-6 p-7">
            <div className="min-w-0 flex-1">
              <p className="eyebrow mb-2">Debate topic</p>
              <h2 className="text-balance font-serif text-2xl leading-snug text-charcoal">
                {topic}
              </h2>
              {isGenerating && !isPaused && (
                <div className="mt-3 flex items-center gap-2.5">
                  <div className="relative h-4 w-4 flex-shrink-0">
                    <div className="absolute inset-0 rounded-full border-2 border-hairline" />
                    <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-charcoal" />
                  </div>
                  <span className="font-mono text-xs text-ink-muted">{currentPhase}</span>
                </div>
              )}
              {isPaused && (
                <p className="mt-3 font-mono text-xs text-ink-muted">Paused</p>
              )}
            </div>

            {/* Controls */}
            {showControls && (
              <div className="flex flex-shrink-0 items-center gap-2">
                {isGenerating && !isPaused && onPause && (
                  <button
                    onClick={onPause}
                    className="rounded-full border border-black/[0.08] bg-white px-3.5 py-2 text-xs font-medium text-charcoal transition-colors hover:border-charcoal/30"
                  >
                    Pause
                  </button>
                )}
                {isPaused && onResume && (
                  <button
                    onClick={onResume}
                    className="rounded-full bg-charcoal px-3.5 py-2 text-xs font-medium text-white transition-colors hover:bg-charcoal/85"
                  >
                    Resume
                  </button>
                )}
                {(isGenerating || isPaused) && onAbort && (
                  <button
                    onClick={onAbort}
                    className="rounded-full border border-black/[0.08] bg-white px-3.5 py-2 text-xs font-medium text-charcoal transition-colors hover:border-charcoal/30"
                    title="Force stop"
                  >
                    Stop
                  </button>
                )}
                {onRegenerate && (
                  <button
                    onClick={onRegenerate}
                    disabled={isGenerating && !isPaused}
                    className="rounded-full border border-black/[0.08] bg-white px-3.5 py-2 text-xs font-medium text-charcoal transition-colors hover:border-charcoal/30 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Regenerate
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Progress strip */}
          <div className="border-t border-hairline px-7 py-4">
            <div className="mb-2.5 flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">
                Progress
              </span>
              <span className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">
                {stagesDone} of 5 stages
              </span>
            </div>
            <div className="flex h-1 overflow-hidden rounded-full bg-hairline">
              <div
                className="bg-charcoal transition-all duration-700 ease-out-expo"
                style={{ width: `${(stagesDone / 5) * 100}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between">
              {['Research', 'Opening', 'Rebuttal', 'Closing', 'Summary'].map((label, i) => {
                const isDone = i < 4 ? Boolean(turnsByPhase[PHASES[i].key]?.pro && turnsByPhase[PHASES[i].key]?.con) : hasSummary;
                const isCurrent =
                  isGenerating && currentPhase.toLowerCase().includes(label.toLowerCase());
                return (
                  <span
                    key={label}
                    className={`font-mono text-[10px] transition-colors duration-500 ${
                      isDone
                        ? 'text-charcoal'
                        : isCurrent
                        ? 'animate-pulse text-charcoal/60'
                        : 'text-charcoal/25'
                    }`}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Stable phase timeline — every stage always on screen ── */}
        <div className="card-frame divide-y divide-black/[0.06] overflow-hidden">
          {PHASES.map(({ key, n, label }) => {
            const status = getPhaseStatus(key);
            const phaseTurns = turnsByPhase[key] ?? { pro: null, con: null };
            const isOpen = status === 'active' || (status === 'complete' && !collapsed[key]);

            return (
              <section key={key}>
                {/* Phase header row — always visible */}
                <button
                  type="button"
                  onClick={status === 'complete' ? () => toggleCollapsed(key) : undefined}
                  className={`flex w-full items-center gap-3 px-6 py-4 text-left transition-colors ${
                    status === 'complete' ? 'cursor-pointer hover:bg-black/[0.02]' : 'cursor-default'
                  }`}
                  aria-expanded={isOpen}
                >
                  <span
                    className={`font-mono text-xs transition-colors duration-500 ${
                      status === 'pending' ? 'text-charcoal/25' : 'text-ink-muted'
                    }`}
                  >
                    {n}
                  </span>
                  <h3
                    className={`flex-1 text-sm font-semibold transition-colors duration-500 ${
                      status === 'pending' ? 'text-charcoal/30' : 'text-charcoal'
                    }`}
                  >
                    {label}
                  </h3>

                  {status === 'pending' && (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-charcoal/25">
                      Queued
                    </span>
                  )}
                  {status === 'active' && (
                    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-charcoal">
                      <span className="relative flex h-2 w-2 overflow-hidden rounded-full">
                        <span className="h-full w-1/2 animate-pulse bg-steel-blue" />
                        <span className="h-full w-1/2 animate-pulse bg-vermilion" />
                      </span>
                      Live
                    </span>
                  )}
                  {status === 'complete' && (
                    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                      <svg className="h-3.5 w-3.5 text-charcoal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <svg
                        className={`h-3.5 w-3.5 text-ink-muted transition-transform duration-300 ease-out-expo ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  )}
                </button>

                {/* Phase content — expands in place, never a separate card */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-1 border-t border-hairline md:grid-cols-2">
                        <SideColumn
                          side="pro"
                          label={proLabel}
                          turn={phaseTurns.pro}
                          streamingText={status === 'active' ? streamingContent.pro : ''}
                          showThinking={status === 'active' && isGenerating}
                        />
                        <SideColumn
                          side="con"
                          label={conLabel}
                          turn={phaseTurns.con}
                          streamingText={status === 'active' ? streamingContent.con : ''}
                          showThinking={status === 'active' && isGenerating}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            );
          })}

          {/* Summary stage — part of the same timeline, foldable like phases */}
          {(() => {
            const summaryOpen =
              summaryStatus === 'active' || (summaryStatus === 'complete' && !collapsed.summary);

            return (
              <section>
                <button
                  type="button"
                  onClick={summaryStatus === 'complete' ? () => toggleCollapsed('summary') : undefined}
                  className={`flex w-full items-center gap-3 px-6 py-4 text-left transition-colors ${
                    summaryStatus === 'complete' ? 'cursor-pointer hover:bg-black/[0.02]' : 'cursor-default'
                  }`}
                  aria-expanded={summaryOpen}
                >
                  <span
                    className={`font-mono text-xs transition-colors duration-500 ${
                      summaryStatus === 'pending' ? 'text-charcoal/25' : 'text-ink-muted'
                    }`}
                  >
                    05
                  </span>
                  <h3
                    className={`flex-1 text-sm font-semibold transition-colors duration-500 ${
                      summaryStatus === 'pending' ? 'text-charcoal/30' : 'text-charcoal'
                    }`}
                  >
                    Summary &amp; Verdict
                  </h3>

                  {summaryStatus === 'pending' && (
                    <span className="font-mono text-[10px] uppercase tracking-wider text-charcoal/25">
                      Queued
                    </span>
                  )}
                  {summaryStatus === 'active' && (
                    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-charcoal">
                      <span className="relative h-3 w-3">
                        <span className="absolute inset-0 rounded-full border border-hairline" />
                        <span className="absolute inset-0 animate-spin rounded-full border border-transparent border-t-charcoal" />
                      </span>
                      Writing
                    </span>
                  )}
                  {summaryStatus === 'complete' && (
                    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                      <svg className="h-3.5 w-3.5 text-charcoal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <svg
                        className={`h-3.5 w-3.5 text-ink-muted transition-transform duration-300 ease-out-expo ${summaryOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  )}
                </button>

                <AnimatePresence initial={false}>
                  {summaryOpen && (
                    <motion.div
                      key="summary-content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-hairline px-6 py-6">
                        {summaryStatus === 'active' ? (
                          <p className="text-center text-sm text-ink-muted">
                            Distilling both cases into a balanced verdict…
                          </p>
                        ) : summaryText ? (
                          <div
                            className="prose prose-sm max-w-none break-words prose-p:text-charcoal/80 prose-p:leading-relaxed prose-p:my-3 prose-strong:text-charcoal prose-strong:font-semibold prose-headings:text-charcoal prose-li:text-charcoal/80"
                            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                          >
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{summaryText}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-center text-sm text-ink-muted">
                            Both sides have made their case. Cast your vote below.
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            );
          })()}
        </div>
      </div>
    </MotionConfig>
  );
};

export default LiveDebate;
